/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ConfirmationDialogConfig,
  ConfirmationDialogConfig$inboundSchema,
  ConfirmationDialogConfig$Outbound,
  ConfirmationDialogConfig$outboundSchema,
} from "./confirmationdialogconfig.js";
import {
  DropdownOption,
  DropdownOption$inboundSchema,
  DropdownOption$Outbound,
  DropdownOption$outboundSchema,
} from "./dropdownoption.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The variant of the button to appear on the dropdown.
 */
export const DropdownComponentActionConfigButtonVariant = {
  Default: "default",
  Tier2: "tier2",
  Tier3: "tier3",
  Brand: "brand",
  Destructive: "destructive",
  Inverse: "inverse",
  Link: "link",
  Transparent: "transparent",
} as const;
/**
 * The variant of the button to appear on the dropdown.
 */
export type DropdownComponentActionConfigButtonVariant = OpenEnum<
  typeof DropdownComponentActionConfigButtonVariant
>;

/**
 * The config of the dropdown
 */
export type DropdownComponentActionConfig = {
  /**
   * The variant of the button to appear on the dropdown.
   */
  buttonVariant?: DropdownComponentActionConfigButtonVariant | undefined;
  /**
   * The config of the confirmation dialog.
   */
  confirmationDialogConfig?: ConfirmationDialogConfig | undefined;
  /**
   * Whether the dropdown is disabled.
   */
  disabled?: boolean | undefined;
  /**
   * The options of the dropdown.
   */
  options: Array<DropdownOption>;
  /**
   * Whether to show the action icon on the currently selected option in the dropdown. If false, the action icon will not be shown.
   */
  showIcon?: boolean | undefined;
  /**
   * The subtitle of the dropdown. This is displayed above the dropdown options.
   */
  subtitle?: string | undefined;
  /**
   * A reference to a reading or setting.
   */
  valueRef: Ref;
};

/** @internal */
export const DropdownComponentActionConfigButtonVariant$inboundSchema:
  z.ZodType<DropdownComponentActionConfigButtonVariant, z.ZodTypeDef, unknown> =
    openEnums.inboundSchema(DropdownComponentActionConfigButtonVariant);
/** @internal */
export const DropdownComponentActionConfigButtonVariant$outboundSchema:
  z.ZodType<string, z.ZodTypeDef, DropdownComponentActionConfigButtonVariant> =
    openEnums.outboundSchema(DropdownComponentActionConfigButtonVariant);

/** @internal */
export const DropdownComponentActionConfig$inboundSchema: z.ZodType<
  DropdownComponentActionConfig,
  z.ZodTypeDef,
  unknown
> = z.object({
  button_variant: DropdownComponentActionConfigButtonVariant$inboundSchema
    .default("tier2"),
  confirmation_dialog_config: types.optional(
    ConfirmationDialogConfig$inboundSchema,
  ),
  disabled: types.boolean().default(false),
  options: z.array(DropdownOption$inboundSchema),
  show_icon: types.boolean().default(false),
  subtitle: types.optional(types.string()),
  value_ref: Ref$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "button_variant": "buttonVariant",
    "confirmation_dialog_config": "confirmationDialogConfig",
    "show_icon": "showIcon",
    "value_ref": "valueRef",
  });
});
/** @internal */
export type DropdownComponentActionConfig$Outbound = {
  button_variant: string;
  confirmation_dialog_config?: ConfirmationDialogConfig$Outbound | undefined;
  disabled: boolean;
  options: Array<DropdownOption$Outbound>;
  show_icon: boolean;
  subtitle?: string | undefined;
  value_ref: Ref$Outbound;
};

/** @internal */
export const DropdownComponentActionConfig$outboundSchema: z.ZodType<
  DropdownComponentActionConfig$Outbound,
  z.ZodTypeDef,
  DropdownComponentActionConfig
> = z.object({
  buttonVariant: DropdownComponentActionConfigButtonVariant$outboundSchema
    .default("tier2"),
  confirmationDialogConfig: ConfirmationDialogConfig$outboundSchema.optional(),
  disabled: z.boolean().default(false),
  options: z.array(DropdownOption$outboundSchema),
  showIcon: z.boolean().default(false),
  subtitle: z.string().optional(),
  valueRef: Ref$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    buttonVariant: "button_variant",
    confirmationDialogConfig: "confirmation_dialog_config",
    showIcon: "show_icon",
    valueRef: "value_ref",
  });
});

export function dropdownComponentActionConfigToJSON(
  dropdownComponentActionConfig: DropdownComponentActionConfig,
): string {
  return JSON.stringify(
    DropdownComponentActionConfig$outboundSchema.parse(
      dropdownComponentActionConfig,
    ),
  );
}
export function dropdownComponentActionConfigFromJSON(
  jsonString: string,
): SafeParseResult<DropdownComponentActionConfig, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => DropdownComponentActionConfig$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'DropdownComponentActionConfig' from JSON`,
  );
}
